<script lang="ts" setup>
import type { TagState } from '@sysarb/sysarb-api/models/entities/WorkTag';
import type WorkTag from '@sysarb/sysarb-api/models/entities/WorkTag';
import { TagGroups } from '@/models/enums/TagGroups';
import type AsyncData from '@sysarb/sysarb-api/models/common/AsyncData';
import MappingWorkTag from '@/components/mappings/analyze/MappingWorkTag.vue';
import Accordion from '@/components/common/accordions/Accordion.vue';
import type MappingEmploymentTag from '@sysarb/sysarb-api/models/entities/MappingEmploymentTag';
import debounce from 'debounce';
import type { PropType} from 'vue';
import { ref, watch } from 'vue';
import type MappingWorkEqualWorksTag from '@sysarb/sysarb-api/models/entities/MappingWorkEqualWorksTag';
import type MappingWorkConnectedWorksTag from '@sysarb/sysarb-api/models/entities/MappingWorkConnectedWorksTag';
import { useUser } from '@/login/composables/useUser';
import { useApi } from '@/common/ApiHelper';
import { useI18n } from 'vue-i18n';

const { isReadOnly } = useUser();
const api = useApi();
const { t } = useI18n();

const props = defineProps({
    selectedWorkTags: {
        type: Array as PropType<(MappingWorkEqualWorksTag | MappingWorkConnectedWorksTag | MappingEmploymentTag)[]>,
        default: () => []
    },
    mappingEmploymentTags: {
        type: Array as PropType<MappingEmploymentTag[]>,
        default: null
    },
    asyncWorkTags: {
        type: Object as PropType<AsyncData<WorkTag[]>>,
        default: null
    },
    readonly: {
        type: Boolean,
        default: false
    },
    showImportAnalyzeText: {
        type: Boolean,
        default: false
    },
    alignVertical: {
        type: Boolean,
        default: false
    },
    useAccordion: {
        type: Boolean,
        default: false
    },
    disableNeutralState: {
        type: Boolean,
        default: false
    },
});

const emit = defineEmits<{
    'toggleTag': [workTag: WorkTag, tagState: TagState | null];
}>();

const groupedWorkTags = ref<Map<string, { cellClass: string; tags: WorkTag[] }>>(new Map<string, { cellClass: string; tags: WorkTag[] }>());

async function initTagGroups(): Promise<void> {
    let workTags: WorkTag[] = [];
    if (props.asyncWorkTags != null) {
        await props.asyncWorkTags.promise;
        workTags = props.asyncWorkTags.data;
    } else {
        workTags = await api.workTag.getBySelection({});
    }

    // unique element groups
    const myGroupedWorkTags: Map<string, { cellClass: string; tags: WorkTag[] }> = new Map<string, { cellClass: string; tags: WorkTag[] }>();
    myGroupedWorkTags.set(TagGroups.Market, { cellClass: 'medium-auto', tags: [] });
    myGroupedWorkTags.set(TagGroups.Individual, { cellClass: 'medium-auto', tags: [] });
    myGroupedWorkTags.set(TagGroups.Work, { cellClass: 'medium-auto', tags: [] });

    // Group workTags
    workTags.forEach(work => {
        if (props.readonly) {
            const hasTags = props.selectedWorkTags.some(x => (x as any).tagID?.id?.isEqualTo(work!.id!) === true)
                || (props.mappingEmploymentTags != null && props.mappingEmploymentTags?.some(x => x.tagID?.id?.isEqualTo(work!.id!) === true) === true);
            if (hasTags) {
                const tagGroup = myGroupedWorkTags.get(work.tagType);

                if (tagGroup != null) {
                    tagGroup.tags.push(work);
                    myGroupedWorkTags.set(work.tagType, tagGroup);
                }
            }
            return;
        }
        if (myGroupedWorkTags.has(work.tagType)) {
            const tagGroup = myGroupedWorkTags.get(work.tagType);

            if (tagGroup != null) {
                tagGroup.tags.push(work);
                myGroupedWorkTags.set(work.tagType, tagGroup);
            }
        }
    });

    groupedWorkTags.value = myGroupedWorkTags;
}

const debounceInitTagGroups = debounce(initTagGroups, 100);

function toggleTag(tag: WorkTag, tagState: TagState | null): void {
    if (!isReadOnly.value) {
        emit('toggleTag', tag, tagState);
    }
}

function translateHeader(tag: string): string {
    switch (tag) {
        case 'tagHeaderIndividual':
            return t('components.mappingWorkTags.tagHeaderIndividual');
        case 'tagHeaderMarket':
            return t('components.mappingWorkTags.tagHeaderMarket');
        case 'tagHeaderWork':
            return t('components.mappingWorkTags.tagHeaderWork');
        default:
            return '';
    }
}

watch([
    (): MappingEmploymentTag[] => props.mappingEmploymentTags,
    (): unknown[] => props.selectedWorkTags
], () => {
    void debounceInitTagGroups();
});

void debounceInitTagGroups();
</script>

<template>
    <div v-if="useAccordion" class="grid-x sy-border-bottom">
        <Accordion
            v-for="(group) in groupedWorkTags"
            :key="group[0]"
            :title="translateHeader(`tagHeader${group[0]}`)"
            :default-state="true"
            class="cell"
        >
            <div class="padding-vertical-3">
                <template v-if="group[1].tags.length === 0">
                    <span class="margin-bottom-6">{{ $t('components.mappingWorkTags.noTags') }}</span>
                </template>
                <MappingWorkTag
                    v-for="(tag, tagIndex) in group[1].tags"
                    :key="tagIndex"
                    :work-tag="tag"
                    :mapping-employment-tags="mappingEmploymentTags"
                    :selected-work-tags="selectedWorkTags"
                    :show-import-analyze-text="showImportAnalyzeText"
                    :readonly="readonly"
                    @change-state="toggleTag"
                />
            </div>
        </Accordion>
    </div>
    <div v-else class="grid-x grid-margin-x">
        <div
            v-for="(group) in groupedWorkTags"
            :key="group[0]"
            :class="['cell' , !alignVertical ? group[1].cellClass : '']"
        >
            <label v-if="group[0] != null" class="margin-bottom-3">{{ translateHeader(`tagHeader${group[0]}`) }}</label>
            <template v-if="group[1].tags.length === 0">
                <p class="margin-bottom-5">{{ $t('components.mappingWorkTags.noTags') }}</p>
            </template>
            <MappingWorkTag
                v-for="(tag, tagIndex) in group[1].tags"
                :key="tagIndex"
                :work-tag="tag"
                :mapping-employment-tags="mappingEmploymentTags"
                :selected-work-tags="selectedWorkTags"
                :show-import-analyze-text="showImportAnalyzeText"
                :readonly="readonly"
                :disable-neutral-state="disableNeutralState"
                @change-state="toggleTag"
            />
        </div>
    </div>
</template>
